import { useRef } from 'react';

export const BLEED = 16;

let _gidc = 0;

// ---- rally roadbook palette ----
const CHALK = '#e8e4da';   // handwritten chalk ink
const YEL = '#f2c14e';     // rally yellow
const YEL_HI = '#f5b81f';  // brighter highlight yellow
const SLATE = '#15140f';   // chalkboard slate
const CREAM = '#d8cfb8';   // map / cream paper
const DIRT = '#3a2c1c';    // dirt speckle / contour brown
const RING = '#3a352a';    // outer slate ring

// Small stopwatch glyph centred at the local origin.
function Stopwatch({ x, y, s = 1, color = YEL }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} stroke={color} strokeWidth={0.6} fill="none">
      <circle cx={0} cy={0.4} r={2.4} />
      <line x1={0} y1={-2.4} x2={0} y2={-3.3} />
      <line x1={-1.1} y1={-3.3} x2={1.1} y2={-3.3} />
      <line x1={0} y1={0.4} x2={0} y2={-1.1} />
    </g>
  );
}

// A small crosshair-circle target glyph centred at (cx,cy).
function Target({ cx, cy, color = YEL }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill="none" stroke={color} strokeWidth={0.7} />
      <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} stroke={color} strokeWidth={0.6} />
      <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 6} stroke={color} strokeWidth={0.6} />
    </g>
  );
}

// "Rally roadbook" frame — a co-driver pace-note page. Dark slate ring, rally-yellow
// ink, torn map-paper corners and dirt speckle. Every mark hugs the border / corners /
// bleed so the centred card content (badge, title, date, location, counts, RSVP) stays
// clear: a taped "SS 07" tag top-left, chalk pace fragments along the top, GPS coords +
// crosshair + torn map + route trace top-right, a left distance ruler with a threading
// road line, a right pace-note tick column (above the Going pill), a boxed "R4 / 200m
// JUNCTION" bottom-left, an italic "PUSH - ATTACK - COMMIT" bottom-centre, a yellow
// "TARGET TIME" tag + slate "DIFF" chip bottom-right, a faint centre viewfinder, and
// scattered dirt speckles.
export default function RallyFrame({ w, h, radius = 12, accent }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `rl${++_gidc}_`;
  const g = idRef.current;

  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;
  const yel = accent || YEL;

  const cx = x + w / 2;       // card centre x
  const right = x + w;        // card right edge
  const bottom = y + h;       // card bottom edge

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`${g}tape`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={YEL_HI} /><stop offset="1" stopColor={yel} />
          </linearGradient>
        </defs>

        {/* ===== slate border ring + faint yellow inner line ===== */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={RING} strokeWidth={2.4} />
        <rect x={x + 2.5} y={y + 2.5} width={w - 5} height={h - 5} rx={Math.max(2, r - 2)} ry={Math.max(2, r - 2)} fill="none" stroke={yel} strokeWidth={0.7} opacity={0.4} />

        {/* ===== CORNERS: torn map-paper fragments + topographic contours + route trace ===== */}
        {/* TL torn paper */}
        <polygon points={`${x},${y} ${x + 54},${y} ${x + 44},${y + 8} ${x + 50},${y + 16} ${x + 34},${y + 22} ${x + 24},${y + 14} ${x},${y + 28}`} fill={CREAM} opacity={0.16} />
        <path d={`M ${x + 4} ${y + 20} C ${x + 16} ${y + 14} ${x + 28} ${y + 16} ${x + 40} ${y + 10}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.4} />
        <path d={`M ${x + 6} ${y + 26} C ${x + 18} ${y + 21} ${x + 32} ${y + 24} ${x + 44} ${y + 17}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.3} />

        {/* TR torn paper + contours + yellow route trace */}
        <polygon points={`${right},${y} ${right - 64},${y} ${right - 54},${y + 10} ${right - 62},${y + 20} ${right - 46},${y + 26} ${right - 32},${y + 18} ${right},${y + 36}`} fill={CREAM} opacity={0.16} />
        <path d={`M ${right - 58} ${y + 14} C ${right - 44} ${y + 10} ${right - 30} ${y + 14} ${right - 10} ${y + 8}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.4} />
        <path d={`M ${right - 54} ${y + 22} C ${right - 40} ${y + 17} ${right - 26} ${y + 21} ${right - 8} ${y + 15}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.3} />
        <path d={`M ${right - 60} ${y + 6} C ${right - 46} ${y + 14} ${right - 28} ${y + 8} ${right - 4} ${y + 18}`} fill="none" stroke={YEL_HI} strokeWidth={0.9} opacity={0.7} strokeDasharray="3 2" />

        {/* BL torn paper */}
        <polygon points={`${x},${bottom} ${x},${bottom - 31} ${x + 20},${bottom - 23} ${x + 14},${bottom - 13} ${x + 28},${bottom - 7} ${x + 22},${bottom}`} fill={CREAM} opacity={0.15} />
        <path d={`M ${x + 4} ${bottom - 21} C ${x + 12} ${bottom - 17} ${x + 18} ${bottom - 21} ${x + 26} ${bottom - 15}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.35} />

        {/* BR torn paper */}
        <polygon points={`${right},${bottom} ${right},${bottom - 33} ${right - 22},${bottom - 25} ${right - 16},${bottom - 15} ${right - 28},${bottom - 9} ${right - 20},${bottom}`} fill={CREAM} opacity={0.15} />
        <path d={`M ${right - 4} ${bottom - 23} C ${right - 12} ${bottom - 19} ${right - 18} ${bottom - 23} ${right - 26} ${bottom - 17}`} fill="none" stroke={DIRT} strokeWidth={0.5} opacity={0.35} />

        {/* ===== TOP-LEFT taped tag: SS 07 / 12.34 km ===== */}
        <g transform={`translate(${x + 12} ${y - 14})`}>
          <rect x={0} y={0} width={58} height={22} rx={1.5} fill={CREAM} opacity={0.92} />
          <polygon points="-4,-3 10,-3 -4,9" fill={yel} opacity={0.85} />
          <polygon points="62,13 48,25 62,25" fill={yel} opacity={0.85} />
          <text x={6} y={13} fontSize={10} fontWeight="800" fill={SLATE} letterSpacing={0.5}>SS 07</text>
          <text x={6} y={19.5} fontSize={5.5} fill={DIRT} letterSpacing={0.3}>12.34 km</text>
        </g>

        {/* ===== TOP edge handwritten pace fragments (italic chalk, low opacity) ===== */}
        <g fill={CHALK} opacity={0.5}>
          <text x={x + 118} y={y - 5} fontSize={7} fontStyle="italic">30 L6</text>
          <rect x={x + 150} y={y - 12} width={9} height={9} rx={1} fill="none" stroke={CHALK} strokeWidth={0.6} />
          <text x={x + 152.5} y={y - 5} fontSize={7} fontWeight="800">!</text>
          <text x={x + 173} y={y - 5} fontSize={7} fontStyle="italic">crest</text>
          <path d={`M ${x + 194} ${y - 6} L ${x + 197} ${y - 11} L ${x + 200} ${y - 6}`} fill="none" stroke={yel} strokeWidth={0.8} opacity={1} />
          <text x={x + 213} y={y - 8.5} fontSize={6} fontStyle="italic">80</text>
          <line x1={x + 212} y1={y - 7} x2={x + 226} y2={y - 7} stroke={CHALK} strokeWidth={0.5} />
          <text x={x + 213} y={y - 1.5} fontSize={6} fontStyle="italic">6R</text>
        </g>

        {/* ===== TOP-RIGHT GPS coords + crosshair target ===== */}
        <text x={x + 284} y={y - 7} fontSize={6} fill={yel} letterSpacing={0.4}>N 45 21.7</text>
        <text x={x + 284} y={y - 0.5} fontSize={6} fill={yel} letterSpacing={0.4}>E 6 52.1</text>
        <Target cx={right - 14} cy={y - 7} color={yel} />

        {/* ===== LEFT edge vertical distance ruler in the left bleed ===== */}
        <g stroke={yel} strokeWidth={0.7} opacity={0.8}>
          {[28, 62, 96, 130, 164].map((dy, i) => (
            <line key={`tk${i}`} x1={x - 7} y1={y + dy} x2={x} y2={y + dy} />
          ))}
        </g>
        <g fill={yel} fontSize={5} opacity={0.9}>
          <text x={x - 14} y={y + 30}>12</text>
          <text x={x - 13} y={y + 64}>9</text>
          <text x={x - 13} y={y + 98}>6</text>
          <text x={x - 13} y={y + 132}>3</text>
          <text x={x - 13} y={y + 166}>0</text>
        </g>
        {/* thin wavy road line threading down past the ruler + yellow segment + ring node */}
        <path d={`M ${x - 5} ${y + 24} C ${x - 10} ${y + 44} ${x - 2} ${y + 64} ${x - 8} ${y + 84} C ${x - 12} ${y + 104} ${x - 3} ${y + 124} ${x - 7} ${y + 149} C ${x - 9} ${y + 164} ${x - 5} ${y + 179} ${x - 6} ${y + 194}`} fill="none" stroke={CHALK} strokeWidth={0.7} opacity={0.4} />
        <path d={`M ${x - 8} ${y + 84} C ${x - 12} ${y + 104} ${x - 3} ${y + 124} ${x - 7} ${y + 149}`} fill="none" stroke={YEL_HI} strokeWidth={1} opacity={0.85} />
        <circle cx={x - 7} cy={y + 149} r={2} fill="none" stroke={YEL_HI} strokeWidth={0.8} />

        {/* ===== RIGHT edge pace-note tick column (upper-right, above the Going pill) ===== */}
        <g>
          <line x1={right - 6} y1={y + 48} x2={right + 7} y2={y + 48} stroke={CHALK} strokeWidth={0.5} opacity={0.6} />
          <text x={right - 6} y={y + 46} fontSize={4.6} fill={CHALK} opacity={0.8} textAnchor="end">6R 80</text>
          <line x1={right - 6} y1={y + 64} x2={right + 7} y2={y + 64} stroke={CHALK} strokeWidth={0.5} opacity={0.6} />
          <text x={right - 6} y={y + 62} fontSize={4.6} fill={CHALK} opacity={0.8} textAnchor="end">L5 60</text>
          <line x1={right - 6} y1={y + 80} x2={right + 7} y2={y + 80} stroke={YEL_HI} strokeWidth={0.8} />
          <text x={right - 6} y={y + 78} fontSize={4.6} fill={yel} textAnchor="end">crest 80</text>
          <line x1={right - 6} y1={y + 96} x2={right + 7} y2={y + 96} stroke={CHALK} strokeWidth={0.5} opacity={0.6} />
          <text x={right - 6} y={y + 94} fontSize={4.6} fill={CHALK} opacity={0.8} textAnchor="end">3R 40</text>
        </g>

        {/* ===== BOTTOM-LEFT boxed R4 tag + 200m JUNCTION ===== */}
        <g>
          <rect x={x + 4} y={bottom + 4} width={13} height={9} rx={1} fill="none" stroke={yel} strokeWidth={0.8} />
          <text x={x + 6} y={bottom + 11} fontSize={6} fontWeight="800" fill={yel}>R4</text>
          <text x={x + 21} y={bottom + 11} fontSize={6} fill={yel} letterSpacing={0.3}>200m JUNCTION</text>
        </g>

        {/* ===== BOTTOM-CENTER handwritten PUSH - ATTACK - COMMIT, underlined ===== */}
        <g>
          <text x={cx} y={bottom + 10.5} fontSize={7.5} fontStyle="italic" fill={CHALK} textAnchor="middle" opacity={0.85}>PUSH - ATTACK - COMMIT</text>
          <line x1={cx - 52} y1={bottom + 13} x2={cx + 52} y2={bottom + 13} stroke={YEL_HI} strokeWidth={0.9} />
        </g>

        {/* ===== BOTTOM-RIGHT yellow TARGET TIME tag + slate DIFF chip w/ stopwatch ===== */}
        <g transform={`translate(${right - 88} ${bottom + 1.5})`}>
          <rect x={0} y={0} width={80} height={6.5} rx={1} fill={`url(#${g}tape)`} />
          <polygon points="-3.5,-1.5 6,-1.5 -3.5,5.5" fill={YEL_HI} opacity={0.85} />
          <text x={5} y={4.9} fontSize={4.8} fontWeight="800" fill={SLATE} letterSpacing={0.2}>TARGET TIME 07:45.0</text>
        </g>
        <g transform={`translate(${right - 80} ${bottom + 8.5})`}>
          <rect x={0} y={0} width={72} height={6.5} rx={1.5} fill={SLATE} stroke={RING} strokeWidth={0.6} />
          <Stopwatch x={5} y={3.4} s={0.85} color={yel} />
          <text x={9.5} y={4.7} fontSize={4.6} fontWeight="800" fill={CHALK} letterSpacing={0.2}>DIFF -00:12.3</text>
        </g>

        {/* ===== CENTER faint viewfinder rect + corner ticks + crosshair ===== */}
        <g opacity={0.12} stroke={CHALK} fill="none">
          <rect x={cx - 46} y={y + 102} width={92} height={60} strokeWidth={0.6} strokeDasharray="4 4" />
          <path d={`M ${cx - 46} ${y + 112} L ${cx - 46} ${y + 102} L ${cx - 36} ${y + 102}`} />
          <path d={`M ${cx + 36} ${y + 102} L ${cx + 46} ${y + 102} L ${cx + 46} ${y + 112}`} />
          <path d={`M ${cx - 46} ${y + 152} L ${cx - 46} ${y + 162} L ${cx - 36} ${y + 162}`} />
          <path d={`M ${cx + 36} ${y + 162} L ${cx + 46} ${y + 162} L ${cx + 46} ${y + 152}`} />
          <line x1={cx - 6} y1={y + 132} x2={cx + 6} y2={y + 132} />
          <line x1={cx} y1={y + 126} x2={cx} y2={y + 138} />
        </g>

        {/* ===== dirt speckle dots scattered around the outer edges ===== */}
        <g fill={DIRT} opacity={0.5}>
          <circle cx={x + 74} cy={y - 10} r={0.8} /><circle cx={x + 104} cy={y - 7} r={0.6} />
          <circle cx={x + 274} cy={y - 10} r={0.7} /><circle cx={x + 4} cy={y + 104} r={0.8} />
          <circle cx={right + 5} cy={y + 134} r={0.7} /><circle cx={x + 44} cy={bottom + 14} r={0.8} />
          <circle cx={right - 46} cy={bottom + 12} r={0.6} /><circle cx={right + 9} cy={y + 44} r={0.7} />
          <circle cx={x - 10} cy={y + 24} r={0.6} /><circle cx={right + 6} cy={y + 184} r={0.7} />
        </g>
      </svg>
    </div>
  );
}
